import React, { useState } from 'react';
import SEO from '../../components/common/SEO';
import LeadForm from '../../components/LeadForm';
import {
  ArrowRight,
  Bank,
  CaretDown,
  CaretUp,
  CheckCircle,
  Coins,
  FileText,
  Gavel,
  Lock,
  MagnifyingGlass,
  Receipt,
  Scales,
  ShieldCheck,
  TrendUp,
  X,
} from '@phosphor-icons/react';

/*
 * Copy on this page describes what the finance and audit service actually does
 * today. Keep it in the customer's language: the operational problem, not the
 * implementation. Re-check against the service before adding a new claim.
 */

const capabilities = [
  {
    label: 'Getting the close done',
    icon: Receipt,
    desc: 'Bank items that have not cleared, cash sitting against no invoice, bills paid twice, currency movements and the recurring accruals someone re-types every month.',
  },
  {
    label: 'Audit and assurance work',
    icon: Gavel,
    desc: 'Engagement files set up, schedules filled, risk and completion papers prepared, and financial statement notes drafted to Singapore audit and reporting standards.',
  },
  {
    label: 'Paperwork that arrives as PDFs',
    icon: FileText,
    desc: 'Purchase orders, credit and debit notes, delivery orders, quotations, expense claims and supplier statements turned into figures your team can check, not re-key.',
  },
  {
    label: 'Knowing you can trust the number',
    icon: Scales,
    desc: 'Every figure is checked before a person sees it and traces back to the record it came from. Review means checking the important items, not doing the work twice.',
  },
];

const workAreas = [
  {
    icon: MagnifyingGlass,
    title: 'The first week of every month',
    desc: 'Bank items that have not cleared, receipts matched to no invoice, bills paid twice, accruals nobody released, and unusual spending are found before the close, not during it.',
  },
  {
    icon: Coins,
    title: 'Money going out and coming in',
    desc: 'What is owed and when it falls due, which payments to make first when cash is tight, how far your receivables have aged, and which customers are about to pay late.',
  },
  {
    icon: TrendUp,
    title: 'Where the margin actually goes',
    desc: 'How dependent you are on your largest customers, how much revenue leaks through discounts and credit notes, and which customers and product lines are genuinely profitable once everything is counted.',
  },
  {
    icon: Bank,
    title: 'Groups and multiple entities',
    desc: 'Balances between your own companies reconciled against each other, currency exposure made visible, and the readiness checks that decide whether consolidation will go smoothly this quarter.',
  },
  {
    icon: TrendUp,
    title: 'Seeing the quarter coming',
    desc: 'Rolling forecasts for revenue, profit and cash, side-by-side scenarios, how far you are off target, and a plain explanation of what changed since last period and why.',
  },
  {
    icon: FileText,
    title: 'Spreadsheets you inherited',
    desc: 'Broken formulas, lookups pointing at the wrong place, typed-over numbers hiding inside totals, duplicate rows and unusual figures are found before they reach a board pack.',
  },
];

const connectedSystems = ['Xero', 'QuickBooks', 'NetSuite', 'Sage', 'Stripe'];

const isFor = [
  { lead: 'Finance teams closing every month across several entities', rest: 'or currencies, where reconciliation is what makes it slow.' },
  { lead: 'Audit and assurance practices', rest: 'that want the engagement file prepared rather than re-typed each season.' },
  { lead: 'Institutions that need financial records to stay under their control', rest: 'with a clear history of every review and approval.' },
];

const isNotFor = [
  'Teams wanting the books updated automatically with nobody checking.',
  'Organisations happy to accept a number they cannot trace back to a source.',
];

const faqs = [
  {
    q: 'What does this actually take off my team?',
    a: 'The preparation. Reconciling bank and intercompany balances, aging what you owe and what you are owed, finding bills paid twice, spotting spending that does not look like a normal month, drafting the recurring accruals, and producing the rolling forecast. Your team arrives to review and decide, instead of to assemble.',
  },
  {
    q: 'Does it work with our accounting system?',
    a: 'Yes. It works with records from Xero, QuickBooks, NetSuite, Sage and Stripe. If companies in your group use different accounting tools, their balances can still be brought together without rebuilding the close in a separate consolidation spreadsheet.',
  },
  {
    q: 'Will it change our books without us knowing?',
    a: 'No. It prepares the work and explains the differences. Any journal or correction is put in front of a person to approve first. Closed periods stay closed, and corrections follow your own rules instead of replacing the original record.',
  },
  {
    q: 'How do we know a number is right?',
    a: 'Every figure traces back to the record it came from, so a reviewer can check it rather than trust it. For audit work, the memos and notes name the standard they rest on. The aim is to make review faster, not to ask you to believe an answer because it looks confident.',
  },
  {
    q: 'What about audit and statutory work?',
    a: 'Engagement files are set up, schedules filled, risk and completion papers prepared, and financial statement notes drafted to Singapore audit and reporting standards. The judgement stays with your team. What changes is how much of the file is ready before they open it.',
  },
  {
    q: 'Can our financial records stay with us?',
    a: 'Yes. Your records can remain in the location and under the access rules your organisation already uses. Every proposed change keeps a clear history of who reviewed it, who approved it and which source supports it.',
  },
];

function FinanceFAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="fsa-faq">
      {faqs.map((faq, i) => {
        const open = openIndex === i;
        const panelId = `fsa-faq-panel-${i}`;
        return (
          <div className="fsa-faq-item" key={faq.q}>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpenIndex(open ? null : i)}
            >
              <span>{faq.q}</span>
              {open
                ? <CaretUp size={20} weight="bold" aria-hidden="true" />
                : <CaretDown size={20} weight="bold" aria-hidden="true" />}
            </button>
            <div id={panelId} hidden={!open}>
              <p>{faq.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FinancialServicesAI() {
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };

  return (
    <>
      <SEO
        title="Faster Month-End Close and Reconciliation | Eagentix"
        description="Reconciliations, aging, duplicate bills, forecasts and audit schedules prepared before your finance team opens the file. Your team reviews exceptions and approves every change."
        pathname="/industries/financial-services-ai-agents"
      />

      <style>{`
        .fsa {
          --fsa-ink: #0f172a;
          --fsa-muted: #475569;
          --fsa-line: #e2e8f0;
          --fsa-soft: #f8fafc;
          --fsa-blue: #0071e3;
          --fsa-radius: 20px;
        }
        .fsa-section { padding: 96px 0; }
        .fsa-section--soft { background: var(--fsa-soft); }
        .fsa-section--white { background: #ffffff; }
        .fsa h2 { font-size: clamp(1.9rem, 3.4vw, 2.5rem); font-weight: 800; color: var(--fsa-ink); letter-spacing: -0.035em; line-height: 1.12; margin: 0 0 14px; }
        .fsa-lede { max-width: 66ch; margin: 0 0 44px; color: var(--fsa-muted); font-size: 1.08rem; line-height: 1.7; }

        .fsa a:focus-visible,
        .fsa button:focus-visible { outline: 3px solid #93c5fd; outline-offset: 3px; border-radius: 12px; }

        /* Hero */
        .fsa-hero { position: relative; overflow: hidden; padding: 96px 0 112px; background: radial-gradient(circle at 76% 32%, rgba(37,99,235,.17), transparent 42%), #020617; }
        .fsa-hero-inner { position: relative; z-index: 3; max-width: 1280px; }
        .fsa-hero-grid { display: grid; grid-template-columns: 1.05fr .95fr; gap: 56px; align-items: center; }
        .fsa-hero h1 { max-width: 22ch; margin: 0 0 22px; color: #ffffff; font-size: clamp(2.4rem, 4.7vw, 3.45rem); font-weight: 900; letter-spacing: -0.04em; line-height: 1.06; }
        .fsa-hero h1 em { font-style: normal; color: #60a5fa; }
        .fsa-hero p { max-width: 60ch; margin: 0 0 34px; color: #cbd5e1; font-size: 1.16rem; font-weight: 500; line-height: 1.7; }
        .fsa-hero-actions { display: flex; gap: 14px; flex-wrap: wrap; }
        .fsa-btn { display: inline-flex; align-items: center; gap: 9px; padding: 16px 32px; border-radius: 12px; font-size: 1.06rem; font-weight: 800; text-decoration: none; transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease; }
        .fsa-btn--primary { color: #ffffff; background: #2563eb; box-shadow: 0 14px 30px -6px rgba(37,99,235,.5); }
        .fsa-btn--primary:hover { transform: translateY(-2px); box-shadow: 0 20px 38px -8px rgba(37,99,235,.58); }
        .fsa-btn--ghost { color: #ffffff; background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.24); }
        .fsa-btn--ghost:hover { background: rgba(255,255,255,.15); }

        .fsa-hero-card { position: relative; width: 100%; max-width: 560px; justify-self: center; }
        .fsa-hero-card-frame { overflow: hidden; border: 1px solid rgba(255,255,255,.18); border-radius: 24px; background: rgba(15,23,42,.9); box-shadow: 0 34px 70px -18px rgba(0,0,0,.62); }
        .fsa-hero-card img { display: block; width: 100%; height: auto; aspect-ratio: 16 / 10; object-fit: cover; object-position: center; }

        /* Proof strip */
        .fsa-proof { background: #0d1a2d; }
        .fsa-proof-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        .fsa-proof-item { padding: 30px 32px; border-left: 1px solid rgba(148,163,184,.2); }
        .fsa-proof-item:first-child { border-left: 0; }
        .fsa-proof-item strong { display: block; color: #ffffff; font-size: 1.6rem; font-weight: 900; letter-spacing: -0.03em; }
        .fsa-proof-item span { display: block; margin-top: 6px; color: #aebed2; font-size: .92rem; line-height: 1.5; }

        /* Capability cards */
        .fsa-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(248px, 1fr)); gap: 20px; }
        .fsa-cap { display: flex; flex-direction: column; gap: 12px; padding: 28px; border: 1px solid var(--fsa-line); border-radius: var(--fsa-radius); background: #ffffff; }
        .fsa-cap-icon { color: var(--fsa-blue); }
        .fsa-cap h3 { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 0; color: var(--fsa-blue); font-size: 1.02rem; font-weight: 800; }
        .fsa-cap p { margin: 0; color: var(--fsa-muted); font-size: .95rem; line-height: 1.65; }

        /* Work areas */
        .fsa-work { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0 56px; border-top: 1px solid var(--fsa-line); }
        .fsa-work-item { display: grid; grid-template-columns: auto 1fr; gap: 16px; padding: 26px 0; border-bottom: 1px solid var(--fsa-line); }
        .fsa-work-item svg { margin-top: 3px; color: var(--fsa-blue); }
        .fsa-work-item h3 { margin: 0 0 7px; color: var(--fsa-ink); font-size: 1.06rem; font-weight: 800; }
        .fsa-work-item p { margin: 0; color: var(--fsa-muted); font-size: .96rem; line-height: 1.65; }

        /* Connected systems */
        .fsa-systems { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 26px; }
        .fsa-systems span { padding: 11px 22px; border: 1px solid var(--fsa-line); border-radius: 999px; background: #ffffff; color: var(--fsa-ink); font-size: 1rem; font-weight: 700; }

        /* Control model */
        .fsa-control { padding: 96px 0; background: #0f172a; }
        .fsa-control h2 { color: #ffffff; }
        .fsa-control .fsa-lede { color: #cbd5e1; }
        .fsa-control-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .fsa-control-card { padding: 28px; border: 1px solid rgba(148,163,184,.28); border-radius: var(--fsa-radius); background: rgba(15,23,42,.7); }
        .fsa-control-card svg { color: #60a5fa; }
        .fsa-control-card strong { display: block; margin: 14px 0 8px; color: #ffffff; font-size: 1.05rem; font-weight: 800; }
        .fsa-control-card p { margin: 0; color: #aebed2; font-size: .95rem; line-height: 1.65; }

        /* Qualification */
        .fsa-fit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; max-width: 950px; margin: 0 auto; }
        .fsa-fit { padding: 34px; border: 1px solid var(--fsa-line); border-radius: var(--fsa-radius); background: #ffffff; }
        .fsa-fit--yes { border: 2px solid #10b981; box-shadow: 0 12px 30px -14px rgba(16,185,129,.35); }
        .fsa-fit h3 { display: flex; align-items: center; gap: 10px; margin: 0 0 22px; font-size: 1.2rem; font-weight: 800; }
        .fsa-fit--yes h3 { color: #047857; }
        .fsa-fit--no h3 { color: #64748b; }
        .fsa-fit-line { display: grid; grid-template-columns: auto 1fr; gap: 11px; margin-top: 15px; font-size: 1rem; line-height: 1.6; }
        .fsa-fit--yes .fsa-fit-line { color: #334155; }
        .fsa-fit--yes .fsa-fit-line svg { color: #10b981; }
        .fsa-fit--no .fsa-fit-line { color: #64748b; }
        .fsa-fit--no .fsa-fit-line svg { color: #94a3b8; }
        .fsa-fit-line svg { margin-top: 2px; }

        /* Practitioner */
        .fsa-practitioner { background: #ffffff; }
        .fsa-practitioner-grid { display: grid; grid-template-columns: 220px 1fr; gap: 48px; align-items: center; max-width: 900px; }
        .fsa-practitioner img { display: block; width: 220px; height: 270px; object-fit: cover; border-radius: var(--fsa-radius); box-shadow: 0 20px 42px -20px rgba(15,23,42,.34); }
        .fsa-practitioner p { max-width: 65ch; color: var(--fsa-muted); font-size: 1.02rem; line-height: 1.7; }
        .fsa-practitioner-points { display: grid; gap: 11px; margin-top: 22px; }
        .fsa-practitioner-point { display: grid; grid-template-columns: auto 1fr; gap: 10px; color: #334155; line-height: 1.5; }
        .fsa-practitioner-point svg { margin-top: 2px; color: var(--fsa-blue); }

        /* Assurance */
        .fsa-assure { padding: 88px 0; background: #020617; border-top: 1px solid rgba(255,255,255,.08); }
        .fsa-assure-inner { max-width: 850px; margin: 0 auto; padding: 48px; border: 1px solid rgba(96,165,250,.45); border-radius: 24px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); box-shadow: 0 28px 56px -20px rgba(0,113,227,.4); text-align: center; }
        .fsa-assure h2 { color: #ffffff; letter-spacing: -0.03em; }
        .fsa-assure p { max-width: 68ch; margin: 0 auto; color: #cbd5e1; font-size: 1.08rem; line-height: 1.72; }

        /* FAQ */
        .fsa-faq { max-width: 860px; }
        .fsa-faq-item { border-bottom: 1px solid #cbd5e1; }
        .fsa-faq-item button { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 0; border: 0; background: transparent; color: var(--fsa-ink); font: inherit; font-size: 1.06rem; font-weight: 800; text-align: left; cursor: pointer; }
        .fsa-faq-item button svg { flex: 0 0 auto; color: var(--fsa-blue); }
        .fsa-faq-item p { max-width: 72ch; margin: -2px 0 22px; color: var(--fsa-muted); font-size: 1rem; line-height: 1.72; }

        /* Cross-link */
        .fsa-xlink { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 32px; padding: 36px 40px; border: 1px solid #dbe4f0; border-radius: var(--fsa-radius); background: #f5f8fc; }
        .fsa-xlink h2 { font-size: clamp(1.5rem, 2.6vw, 1.8rem); margin-bottom: 10px; }
        .fsa-xlink p { max-width: 62ch; margin: 0; color: var(--fsa-muted); font-size: 1.04rem; line-height: 1.65; }
        .fsa-xlink-cta { display: inline-flex; align-items: center; gap: 9px; padding: 17px 30px; border-radius: 12px; background: #2563eb; color: #ffffff; font-size: 1.04rem; font-weight: 800; text-decoration: none; white-space: nowrap; box-shadow: 0 14px 30px -8px rgba(37,99,235,.45); transition: transform .18s ease, background-color .18s ease; }
        .fsa-xlink-cta:hover { background: #1d4ed8; transform: translateY(-2px); }

        /* Contact */
        .fsa-contact { padding: 96px 0; background: #020617; }
        .fsa-contact-inner { max-width: 700px; margin: 0 auto; }
        .fsa-contact h2 { color: #ffffff; text-align: center; }
        .fsa-contact-lede { max-width: 60ch; margin: 0 auto 40px; color: #94a3b8; font-size: 1.1rem; line-height: 1.7; text-align: center; }
        .fsa-ps { margin-top: 36px; padding: 24px; border: 1px dashed #60a5fa; border-radius: 16px; background: rgba(30,41,59,.8); text-align: center; }
        .fsa-ps p { margin: 0; color: #94a3b8; font-size: .95rem; font-style: italic; line-height: 1.65; }
        .fsa-ps strong { color: #60a5fa; font-style: normal; }
        .fsa-contact-alt { margin: 22px 0 0; color: #64748b; font-size: .92rem; line-height: 1.6; text-align: center; }
        .fsa-contact-alt a { color: #93c5fd; }

        @media (max-width: 1000px) {
          .fsa-proof-grid { grid-template-columns: repeat(2, 1fr); }
          .fsa-proof-item:nth-child(3) { border-left: 0; }
          .fsa-proof-item:nth-child(n+3) { border-top: 1px solid rgba(148,163,184,.2); }
          .fsa-control-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 900px) {
          .fsa-hero-grid, .fsa-fit-grid { grid-template-columns: 1fr; }
          .fsa-work { grid-template-columns: 1fr; gap: 0; }
          .fsa-hero-card { max-width: 520px; }
          .fsa-practitioner-grid { grid-template-columns: 1fr; gap: 30px; }
          .fsa-practitioner img { width: 180px; height: 220px; }
        }
        @media (max-width: 640px) {
          .fsa-section, .fsa-contact, .fsa-control { padding: 64px 0; }
          .fsa-hero { padding: 84px 0 76px; }
          .fsa-assure { padding: 64px 0; }
          .fsa-assure-inner, .fsa-fit, .fsa-xlink { padding: 28px 24px; }
          .fsa-proof-grid { grid-template-columns: 1fr; }
          .fsa-proof-item { border-left: 0; border-top: 1px solid rgba(148,163,184,.2); }
          .fsa-proof-item:first-child { border-top: 0; }
          .fsa-btn { width: 100%; justify-content: center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .fsa *, .fsa *::before, .fsa *::after { transition-duration: .01ms !important; }
        }
      `}</style>

      <div className="fsa">
        {/* Hero */}
        <section className="fsa-hero">
          <div style={{ ...container, ...{ maxWidth: '1280px' } }} className="fsa-hero-inner">
            <div className="fsa-hero-grid">
              <div>
                <h1>Month-end starts with <em>review</em>, not assembly.</h1>
                <p>
                  Reconciliations, aging, forecasts and audit schedules arrive prepared. Your team
                  reviews the exceptions and approves every change.
                </p>
                <div className="fsa-hero-actions">
                  <a className="fsa-btn fsa-btn--primary" href="#contact">
                    Book a Finance Session
                    <ArrowRight size={19} weight="bold" aria-hidden="true" />
                  </a>
                  <a className="fsa-btn fsa-btn--ghost" href="#capabilities">See What Arrives Ready</a>
                </div>
              </div>

              <div className="fsa-hero-card">
                <div className="fsa-hero-card-frame">
                  <img
                    src="/images/financial-services-close-review.webp"
                    alt="Finance leader reviewing a prepared month-end close pack with exceptions isolated for approval"
                    width="560"
                    height="350"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Proof strip */}
        <section className="fsa-proof" aria-label="What the finance team receives">
          <div style={container}>
            <div className="fsa-proof-grid">
              <div className="fsa-proof-item">
                <strong>Close work ready to review</strong>
                <span>Reconciliations, aging and exceptions are prepared before your team opens the file on day one.</span>
              </div>
              <div className="fsa-proof-item">
                <strong>Your existing accounting records</strong>
                <span>Works with the records you already keep, even when different companies use different accounting tools.</span>
              </div>
              <div className="fsa-proof-item">
                <strong>Your team approves every change</strong>
                <span>The work is prepared and explained. Nothing reaches the books without a person approving it.</span>
              </div>
              <div className="fsa-proof-item">
                <strong>Every number can be checked</strong>
                <span>Each figure points back to the record it came from, so a reviewer can verify it quickly.</span>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="fsa-section fsa-section--soft" id="capabilities">
          <div style={container}>
            <h2>What arrives ready for review</h2>
            <p className="fsa-lede">
              The recurring finance work is prepared with the differences and source records shown.
              Your team starts with review, not assembly.
            </p>
            <div className="fsa-grid">
              {capabilities.map(({ label, desc, icon: Icon }) => (
                <article className="fsa-cap" key={label}>
                  <Icon className="fsa-cap-icon" size={26} weight="duotone" aria-hidden="true" />
                  <h3>{label}</h3>
                  <p>{desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Work areas */}
        <section className="fsa-section fsa-section--white">
          <div style={container}>
            <h2>What no longer fills the first week of the month</h2>
            <p className="fsa-lede">
              Your team starts with exceptions and decisions, not blank schedules and scattered source files.
            </p>
            <div className="fsa-work">
              {workAreas.map(({ title, desc, icon: Icon }) => (
                <div className="fsa-work-item" key={title}>
                  <Icon size={24} weight="duotone" aria-hidden="true" />
                  <div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 style={{ margin: '48px 0 0', fontSize: '1.06rem', fontWeight: 800, color: '#0f172a' }}>
              Works with the accounting tools you already use
            </h3>
            <div className="fsa-systems">
              {connectedSystems.map((sys) => <span key={sys}>{sys}</span>)}
            </div>
            <p style={{ margin: '18px 0 0', color: '#64748b', fontSize: '.95rem', lineHeight: 1.65, maxWidth: '66ch' }}>
              If companies in the group use different accounting tools, their records can still be brought
              together. Intercompany balances reconcile without rebuilding the close by hand.
            </p>
          </div>
        </section>

        {/* Control model */}
        <section className="fsa-control">
          <div style={container}>
            <h2>Your team stays in control of the books</h2>
            <p className="fsa-lede">
              The work arrives prepared for review. No journal, correction or closed period changes without approval.
            </p>
            <div className="fsa-control-grid">
              <div className="fsa-control-card">
                <MagnifyingGlass size={26} weight="duotone" aria-hidden="true" />
                <strong>Exceptions, not hidden changes</strong>
                <p>
                  Items that do not tie are identified and explained. The books do not change while your
                  team is reviewing the work.
                </p>
              </div>
              <div className="fsa-control-card">
                <ShieldCheck size={26} weight="duotone" aria-hidden="true" />
                <strong>Your team approves the entry</strong>
                <p>
                  Anything that would touch the books is put in front of a person first. Closed periods
                  stay closed, and corrections follow your own rules rather than overwriting history.
                </p>
              </div>
              <div className="fsa-control-card">
                <Lock size={26} weight="duotone" aria-hidden="true" />
                <strong>Your records stay under your control</strong>
                <p>
                  Financial records stay in the location your organisation chooses. Every proposed change
                  keeps the reviewer, approver and supporting source together.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Qualification */}
        <section className="fsa-section fsa-section--soft">
          <div style={container}>
            <h2 style={{ textAlign: 'center' }}>Best suited to finance teams with real review pressure</h2>
            <p className="fsa-lede" style={{ margin: '0 auto 44px', textAlign: 'center' }}>
              A good fit has recurring work, a clear reviewer and a result everyone can check.
            </p>

            <div className="fsa-fit-grid">
              <div className="fsa-fit fsa-fit--yes">
                <h3><ShieldCheck size={24} weight="duotone" aria-hidden="true" />Who this is for</h3>
                {isFor.map((item) => (
                  <div className="fsa-fit-line" key={item.lead}>
                    <CheckCircle size={20} weight="fill" aria-hidden="true" />
                    <span><strong>{item.lead}</strong> {item.rest}</span>
                  </div>
                ))}
              </div>

              <div className="fsa-fit fsa-fit--no">
                <h3><X size={22} weight="bold" aria-hidden="true" />Who this is not for</h3>
                {isNotFor.map((item) => (
                  <div className="fsa-fit-line" key={item}>
                    <X size={20} weight="bold" aria-hidden="true" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Practitioner */}
        <section className="fsa-section fsa-practitioner">
          <div style={container} className="fsa-practitioner-grid">
            <img src="/images/abhijit-ghosh.jpg" alt="Abhijit Ghosh, Founder of Eagentix" />
            <div>
              <h2>The person who scopes the work leads the first session.</h2>
              <p>
                Abhijit Ghosh has 25 years of experience across banking, insurance and regulated
                enterprise work. The discussion stays on close delays, reviewer effort, control points
                and a result your finance team can measure.
              </p>
              <div className="fsa-practitioner-points">
                <div className="fsa-practitioner-point"><CheckCircle size={20} weight="fill" aria-hidden="true" /><span>25 years in regulated enterprise delivery</span></div>
                <div className="fsa-practitioner-point"><CheckCircle size={20} weight="fill" aria-hidden="true" /><span>Banking, insurance, finance and audit experience</span></div>
                <div className="fsa-practitioner-point"><CheckCircle size={20} weight="fill" aria-hidden="true" /><span>The result and approval rules are agreed before work begins</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* Assurance */}
        <section className="fsa-assure">
          <div style={container}>
            <div className="fsa-assure-inner">
              <Bank size={40} weight="duotone" color="#60a5fa" aria-hidden="true" />
              <h2 style={{ margin: '14px 0 16px' }}>Agree the result before the work starts.</h2>
              <p>
                We agree in writing which reconciliations must balance, which reports must be ready and
                what evidence the reviewer must see. If the first finance job does not meet that definition,
                you do not pay for it.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="fsa-section fsa-section--soft">
          <div style={container}>
            <h2>Questions finance leaders ask before the first session</h2>
            <p className="fsa-lede">
              Clear answers on scope, approvals, records and audit work.
            </p>
            <FinanceFAQ />
          </div>
        </section>

        {/* Cross-link */}
        <section className="fsa-section fsa-section--white" style={{ paddingTop: 0 }}>
          <div style={container}>
            <div className="fsa-xlink">
              <div style={{ flex: '1 1 420px' }}>
                <h2>Which finance job should move first?</h2>
                <p>
                  In three hours, we map and rank the three finance jobs consuming the most time. You leave
                  with a written recommendation for the best first move.
                </p>
              </div>
              <a className="fsa-xlink-cta" href="#contact">
                Book a Finance Session
                <ArrowRight size={19} weight="bold" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="fsa-contact" id="contact">
          <div style={{ ...container, ...{ maxWidth: '700px' } }} className="fsa-contact-inner">
            <h2>Start with the finance job that costs you most</h2>
            <p className="fsa-contact-lede">
              Tell us where your close stalls. We reply within one business day to prepare a working
              session with our Singapore-based team.
            </p>

            <LeadForm
              page="/industries/financial-services-ai-agents"
              showTeamSize
              teamSizeLabel="Finance or audit team size"
              title="Book a finance session"
              subtitle="Three required fields. We reply within one business day."
              buttonLabel="Book a Finance Session"
              messageLabel="Where does your close stall? (optional)"
              messagePlaceholder="e.g. Intercompany reconciliation across four entities takes six days..."
              successText="Thanks. We will respond within one business day to prepare a working session for your team."
            />

            <p className="fsa-contact-alt">
              Prefer email? <a href="mailto:contact@eagentix.com">contact@eagentix.com</a>
            </p>

            <div className="fsa-ps">
              <p>
                <strong>P.S.</strong> Your financial records can remain under your organisation's existing
                access rules, covered by a signed Non-Disclosure Agreement and available for auditor review.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
